/**
 * How to actually get a notification (#1334).
 *
 * ⚠️ Written because the happy path is not enough. The first real attempt had
 * a valid subscription, a working service worker, and Apple returning 201 —
 * and no notification arrived, because the steps AFTER granting permission
 * (open from the home screen, allow it in iOS Settings) are invisible and
 * nothing was telling anybody about them.
 *
 * So this is not marketing copy. It is the sequence, per platform, with the
 * failure each step causes when it is skipped.
 */
import { useState } from "react";
import { isIOS, isStandalone } from "../lib/install";

interface Step {
  do: string;
  why?: string;
}

const IOS_STEPS: Step[] = [
  {
    do: "Open twoplates.me in Safari.",
    why: "Chrome and Firefox on iPhone cannot install a web app. Apple only allows Safari to.",
  },
  {
    do: "Tap Share (the square with an arrow, at the bottom), then Add to Home Screen, then Add.",
    why: "Apple permits notifications only to apps added this way. There is no button we can give you for it.",
  },
  {
    do: "Close Safari. Open Two Plates from the new icon on your home screen.",
    why: "If you keep using it in Safari it is a different thing to your phone, and it will never be allowed to notify you.",
  },
  { do: "Kitchen, then Reminders, then Turn reminders on. Tap Allow when your phone asks." },
  {
    do: "Tap Send me one now.",
    why: "If nothing arrives, the next section is what to check.",
  },
];

const ANDROID_STEPS: Step[] = [
  {
    do: "Open twoplates.me in Chrome.",
    why: "Any Chromium browser works. Firefox does too.",
  },
  {
    do: "Tap Add to your home screen when it appears — or Chrome's menu, then Install app.",
    why: "Not strictly required on Android, but it keeps the app out of a tab you will close.",
  },
  { do: "Kitchen, then Reminders, then Turn reminders on. Tap Allow." },
  { do: "Tap Send me one now." },
];

const IOS_FIXES: Step[] = [
  {
    do: "Make sure you opened it from the home-screen icon, not from Safari.",
    why: "This is the most common one by far.",
  },
  {
    do: "Settings, Notifications, Two Plates — make sure Allow Notifications is on.",
    why: "iOS can grant permission in the browser and still keep the app's notifications switched off.",
  },
  {
    do: "If you use a Focus — Work, Sleep, anything — add Two Plates to its allowed apps.",
    why:
      "A Focus silences a notification without telling you or the app. It still arrived, and it is " +
      "waiting in Notification Centre. Worth allowing for the slow-cooker one, which is useless an hour late.",
  },
  {
    do: "If you added it to the home screen before today, remove the icon and add it again.",
    why: "An older copy may be running without the part that displays notifications.",
  },
];

const ANDROID_FIXES: Step[] = [
  { do: "Settings, Apps, Chrome, Notifications — check twoplates.me is allowed." },
  {
    do: "If Do Not Disturb is on, add Two Plates to its exceptions.",
    why: "It silences the notification without telling you. It still arrived — pull down to see it.",
  },
  { do: "Battery optimisation can delay a push by minutes on some phones." },
];

export function SetUpNotifications() {
  const ios = isIOS();
  const installed = isStandalone();
  const [showFixes, setShowFixes] = useState(false);

  const steps = ios ? IOS_STEPS : ANDROID_STEPS;
  const fixes = ios ? IOS_FIXES : ANDROID_FIXES;

  return (
    <section className="border-line rounded-xl border p-5">
      <h2 className="font-semibold">Set up notifications</h2>
      <p className="text-muted mt-1 text-xs">
        {ios ? "On iPhone and iPad" : "On Android"}
        {installed && " · you have already installed it"}
      </p>

      <ol className="mt-4 flex flex-col gap-3">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3">
            <span
              aria-hidden
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
              style={{ background: "var(--color-line)", color: "var(--color-ink)" }}
            >
              {i + 1}
            </span>
            <span className="min-w-0">
              <span className="block text-sm">{s.do}</span>
              {s.why && <span className="text-muted mt-0.5 block text-xs">{s.why}</span>}
            </span>
          </li>
        ))}
      </ol>

      <button
        onClick={() => setShowFixes((v) => !v)}
        aria-expanded={showFixes}
        className="text-plate-b mt-4 text-sm underline underline-offset-4"
      >
        {showFixes ? "Hide" : "Nothing arrived — what to check"}
      </button>

      {showFixes && (
        <>
          <ul className="mt-3 flex flex-col gap-2.5">
            {fixes.map((f, i) => (
              <li key={i} className="border-line rounded-lg border p-3">
                <p className="text-sm">{f.do}</p>
                {f.why && <p className="text-muted mt-0.5 text-xs">{f.why}</p>}
              </li>
            ))}
          </ul>
          <p className="text-muted mt-3 text-xs">
            {/* Said plainly, because it is true and it saves somebody an hour
                of blaming their phone. */}
            If <em>Send me one now</em> says it sent and nothing appears, the
            problem is on the phone rather than here — the list above is in the
            order it is usually one of them.
          </p>
        </>
      )}

      {ios && !installed && (
        <p className="text-plate-a mt-4 text-xs">
          Your phone says this is still running in Safari, so step 3 has not
          happened yet. Nothing will arrive until it does.
        </p>
      )}
    </section>
  );
}
